/**
 * Hit testing for CanvasKit editor.
 * All functions are pure -- no CanvasKit dependency needed.
 */
import type { SceneDocument, SceneNode } from '@/lib/scene-document';
import { computeLayout } from './canvaskit-layout';

// ─── Types ────────────────────────────────────────────────────────────────

export interface WorldBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface HitTestResult {
  /** The deepest node hit */
  hitId: string;
  /** The childId at this level that contained the hit — used by the caller
   *  to decide how to set selectableId. Internal to the recursion; consumers
   *  should only read hitId / selectableId. */
  immediateChildId?: string;
  /** The top-level selectable ancestor (direct child of root or entered group) */
  selectableId: string;
}

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// ─── SVG path bounds ─────────────────────────────────────────────────────

/**
 * Compute the bounding box of an SVG path string.
 * Uses control-point hull (not exact curve extrema), which is a slight
 * overestimate but perfectly adequate for selection boxes and hit testing.
 */
export function getSvgPathBounds(pathData: string): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;

  function track(x: number, y: number) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  // Tokenize: split on command letters, keeping the letter
  const tokens = pathData.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!tokens) return { x: 0, y: 0, width: 0, height: 0 };

  for (const token of tokens) {
    const cmd = token[0];
    const nums = token.slice(1).trim().match(/-?\d+(?:\.\d+)?/g)?.map(Number) ?? [];

    switch (cmd) {
      case 'M':
        for (let i = 0; i < nums.length; i += 2) {
          cx = nums[i]; cy = nums[i + 1];
          track(cx, cy);
          if (i === 0) { startX = cx; startY = cy; }
        }
        break;
      case 'm':
        for (let i = 0; i < nums.length; i += 2) {
          cx += nums[i]; cy += nums[i + 1];
          track(cx, cy);
          if (i === 0) { startX = cx; startY = cy; }
        }
        break;
      case 'L':
        for (let i = 0; i < nums.length; i += 2) {
          cx = nums[i]; cy = nums[i + 1]; track(cx, cy);
        }
        break;
      case 'l':
        for (let i = 0; i < nums.length; i += 2) {
          cx += nums[i]; cy += nums[i + 1]; track(cx, cy);
        }
        break;
      case 'H':
        for (const n of nums) { cx = n; track(cx, cy); }
        break;
      case 'h':
        for (const n of nums) { cx += n; track(cx, cy); }
        break;
      case 'V':
        for (const n of nums) { cy = n; track(cx, cy); }
        break;
      case 'v':
        for (const n of nums) { cy += n; track(cx, cy); }
        break;
      case 'C':
        for (let i = 0; i < nums.length; i += 6) {
          track(nums[i], nums[i + 1]);
          track(nums[i + 2], nums[i + 3]);
          cx = nums[i + 4]; cy = nums[i + 5]; track(cx, cy);
        }
        break;
      case 'c':
        for (let i = 0; i < nums.length; i += 6) {
          track(cx + nums[i], cy + nums[i + 1]);
          track(cx + nums[i + 2], cy + nums[i + 3]);
          cx += nums[i + 4]; cy += nums[i + 5]; track(cx, cy);
        }
        break;
      case 'S':
        for (let i = 0; i < nums.length; i += 4) {
          track(nums[i], nums[i + 1]);
          cx = nums[i + 2]; cy = nums[i + 3]; track(cx, cy);
        }
        break;
      case 's':
        for (let i = 0; i < nums.length; i += 4) {
          track(cx + nums[i], cy + nums[i + 1]);
          cx += nums[i + 2]; cy += nums[i + 3]; track(cx, cy);
        }
        break;
      case 'Q':
        for (let i = 0; i < nums.length; i += 4) {
          track(nums[i], nums[i + 1]);
          cx = nums[i + 2]; cy = nums[i + 3]; track(cx, cy);
        }
        break;
      case 'q':
        for (let i = 0; i < nums.length; i += 4) {
          track(cx + nums[i], cy + nums[i + 1]);
          cx += nums[i + 2]; cy += nums[i + 3]; track(cx, cy);
        }
        break;
      case 'T':
        for (let i = 0; i < nums.length; i += 2) {
          cx = nums[i]; cy = nums[i + 1]; track(cx, cy);
        }
        break;
      case 't':
        for (let i = 0; i < nums.length; i += 2) {
          cx += nums[i]; cy += nums[i + 1]; track(cx, cy);
        }
        break;
      case 'A':
        for (let i = 0; i < nums.length; i += 7) {
          cx = nums[i + 5]; cy = nums[i + 6]; track(cx, cy);
          // Conservative: include arc radii extent
          track(cx - nums[i], cy - nums[i + 1]);
          track(cx + nums[i], cy + nums[i + 1]);
        }
        break;
      case 'a':
        for (let i = 0; i < nums.length; i += 7) {
          cx += nums[i + 5]; cy += nums[i + 6]; track(cx, cy);
          track(cx - nums[i], cy - nums[i + 1]);
          track(cx + nums[i], cy + nums[i + 1]);
        }
        break;
      case 'Z':
      case 'z':
        cx = startX; cy = startY;
        break;
    }
  }

  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * For PATH nodes, get the offset and size of the actual path content
 * relative to the node's (x, y) origin. Returns null for non-PATH nodes.
 */
function getPathVisualOffset(node: SceneNode): Rect | null {
  if (node.type !== 'PATH' || !node.pathData) return null;
  return getSvgPathBounds(node.pathData);
}

// ─── Transform helpers ───────────────────────────────────────────────────

function rotatePoint(px: number, py: number, cx: number, cy: number, angleDeg: number): Point {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = px - cx;
  const dy = py - cy;
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
}

/**
 * Transform a point from parent space into a node's local space.
 * Reverses the node's translation and rotation.
 */
function toLocalSpace(point: Point, node: SceneNode): Point {
  let localX = point.x - node.x;
  let localY = point.y - node.y;

  if (node.rotation) {
    const cx = node.width / 2;
    const cy = node.height / 2;
    const rotated = rotatePoint(localX, localY, cx, cy, -node.rotation);
    localX = rotated.x;
    localY = rotated.y;
  }

  return { x: localX, y: localY };
}

/**
 * Estimate effective height for a TEXT node that has height=0.
 * Text height is auto-calculated at render time but not always persisted,
 * so blueprints often have height=0 for text nodes.
 */
function estimateTextHeight(node: SceneNode): number {
  if (!node.text) return node.fontSize ?? 16;
  const fontSize = node.fontSize ?? 16;
  const lineHeight = node.lineHeight ?? 1.2;
  const lineSize = fontSize * lineHeight;

  // Count explicit line breaks
  const explicitLines = (node.text.match(/\n/g)?.length ?? 0) + 1;

  // Estimate word-wrapped lines if width is known
  if (node.width > 0) {
    const avgCharWidth = fontSize * 0.55;
    const charsPerLine = Math.max(1, Math.floor(node.width / avgCharWidth));
    const wrappedLines = Math.ceil(node.text.length / charsPerLine);
    return Math.max(explicitLines, wrappedLines) * lineSize;
  }

  return explicitLines * lineSize;
}

/**
 * Check if a local-space point is within a node's bounds (0,0 to width,height).
 */
function isInsideBounds(local: Point, node: SceneNode): boolean {
  const h = node.height > 0 ? node.height : (node.type === 'TEXT' ? estimateTextHeight(node) : node.height);
  return (
    local.x >= 0 &&
    local.x <= node.width &&
    local.y >= 0 &&
    local.y <= h
  );
}

/**
 * Check if a local-space point is inside an ellipse inscribed in the node bounds.
 */
function isInsideEllipse(local: Point, node: SceneNode): boolean {
  const cx = node.width / 2;
  const cy = node.height / 2;
  const dx = (local.x - cx) / cx;
  const dy = (local.y - cy) / cy;
  return dx * dx + dy * dy <= 1;
}

// ─── Hit testing ──────────────────────────────────────────────────────────

/**
 * Hit test a point against the scene.
 * Returns the deepest hit node and the top-level selectable ancestor.
 *
 * @param enteredGroupId - If the user has "entered" a group (like Figma double-click),
 *   children of that group become directly selectable.
 * @param linkZoneMode - When true, only link-zone nodes are hittable (for zone editing).
 */
export function hitTest(
  doc: SceneDocument,
  scenePoint: Point,
  enteredGroupId?: string | null,
  linkZoneMode?: boolean,
): HitTestResult | null {
  // Hit testing must read from the same coordinates the renderer uses.
  // For auto-layout containers, computeLayout repositions children — without
  // running it here the click target is misaligned by the layout delta.
  const laidOut = computeLayout(doc);

  if (linkZoneMode) {
    return hitTestLinkZones(laidOut, scenePoint);
  }

  if (enteredGroupId) {
    const group = laidOut.nodes[enteredGroupId];
    if (group?.children) {
      const localPoint = toLocalSpaceChain(laidOut, scenePoint, enteredGroupId);
      const result = hitTestChildren(laidOut, group.children, localPoint, enteredGroupId);
      if (result) return result;
    }
  }

  return hitTestChildren(laidOut, laidOut.rootChildren, scenePoint, null);
}

/**
 * Hit test only link-zone children. Returns the individual zone node as selectable.
 */
function hitTestLinkZones(doc: SceneDocument, scenePoint: Point): HitTestResult | null {
  // Find the link-zone group. Matches both the legacy slotRole='link-zone'
  // GROUP convention and the new first-class LINK_ZONE_GROUP node type.
  for (const id of doc.rootChildren) {
    const group = doc.nodes[id];
    if (!group || !group.children?.length) continue;
    if (group.slotRole !== 'link-zone' && group.type !== 'LINK_ZONE_GROUP') continue;

    const localPoint = toLocalSpace(scenePoint, group);

    // Test zone children in reverse (top-first)
    for (let i = group.children.length - 1; i >= 0; i--) {
      const childId = group.children[i];
      const node = doc.nodes[childId];
      if (!node || node.visible === false) continue;

      const childLocal = toLocalSpace(localPoint, node);
      if (isInsideBounds(childLocal, node)) {
        return { hitId: childId, selectableId: childId, immediateChildId: childId };
      }
    }
  }
  return null;
}

/**
 * Transform a scene-space point into the local space of a node,
 * walking up the parent chain to accumulate transforms.
 */
function toLocalSpaceChain(doc: SceneDocument, scenePoint: Point, nodeId: string): Point {
  // Build chain from root to node
  const chain: SceneNode[] = [];
  let current: SceneNode | undefined = doc.nodes[nodeId];
  while (current) {
    chain.unshift(current);
    current = current.parentId ? doc.nodes[current.parentId] : undefined;
  }

  let point = { ...scenePoint };
  for (const node of chain) {
    point = toLocalSpace(point, node);
  }
  return point;
}

function hitTestChildren(
  doc: SceneDocument,
  childIds: string[],
  parentPoint: Point,
  enteredGroupId: string | null,
): HitTestResult | null {
  // Reverse iteration -- top-most (last in array) first
  for (let i = childIds.length - 1; i >= 0; i--) {
    const childId = childIds[i];
    const node = doc.nodes[childId];
    if (!node || node.visible === false || node.locked || node.slotRole === 'link-zone') continue;
    if (node.type === 'LINK_ZONE_GROUP' || node.type === 'LINK_ZONE') continue;

    const localPoint = toLocalSpace(parentPoint, node);

    // For groups/frames: test children first, then the group itself
    if (node.type === 'GROUP' || node.type === 'FRAME') {
      // A clipping frame clips hit-testing along with rendering (Figma
      // semantics): children pixels outside the frame aren't drawn, so they
      // must not be clickable. Without this, an imported background image
      // that overhangs the artboard by hundreds of px grabs every click
      // around the email and the user can never click empty canvas to
      // deselect.
      if (node.clipContent && !isInsideBounds(localPoint, node)) continue;
      // Auto-layout containers are transparent for selection ONLY when the
      // immediate child that was hit is itself a container — that lets you
      // click a sub-frame directly. If a leaf (text/image/shape) was hit,
      // this group is the smallest container that wraps it, so we select
      // the group. Otherwise the outermost group wins until the user
      // enters it via double-click.
      const isAutoLayout = node.layoutMode === 'VERTICAL' || node.layoutMode === 'HORIZONTAL';
      // Transparent wrappers (no own paint) emitted by the HTML compiler
      // shouldn't trap selection — if the inner hit is itself a container,
      // skip through to it. Without this, an article-card sitting inside
      // an unstyled positioning panel can't be clicked directly.
      const hasVisibleFill = !!node.fills?.some((f) => (f as { visible?: boolean }).visible !== false);
      const hasVisibleStroke = !!node.strokes?.some((s) => (s as { visible?: boolean }).visible !== false);
      const isTransparentWrapper = !hasVisibleFill && !hasVisibleStroke;
      if (node.children?.length) {
        const childHit = hitTestChildren(doc, node.children, localPoint, enteredGroupId);
        if (childHit) {
          const innerImmediate = childHit.immediateChildId
            ? doc.nodes[childHit.immediateChildId]
            : null;
          const innerIsContainer =
            innerImmediate?.type === 'GROUP' || innerImmediate?.type === 'FRAME';
          // Workspace canvas wrappers (frameCanvasId set) are purely structural
          // — opaque white positioning frames around an email/ad. A single click
          // should pass through them to grab the inner section directly, not
          // select the whole email. Standalone canvases never set frameCanvasId,
          // so editor selection semantics there are unchanged.
          const isWorkspaceWrapper = !!node.frameCanvasId;
          const passThrough =
            enteredGroupId === node.id ||
            ((isAutoLayout || isTransparentWrapper || isWorkspaceWrapper) && innerIsContainer);
          return {
            hitId: childHit.hitId,
            selectableId: passThrough ? childHit.selectableId : childId,
            immediateChildId: childId,
          };
        }
      }
      // Test group background
      if (node.fills?.length && isInsideBounds(localPoint, node)) {
        return { hitId: childId, selectableId: childId, immediateChildId: childId };
      }
      continue;
    }

    // Ellipse: inscribed ellipse hit test
    if (node.type === 'ELLIPSE') {
      if (isInsideEllipse(localPoint, node)) {
        return { hitId: childId, selectableId: childId, immediateChildId: childId };
      }
      continue;
    }

    // All types (including PATH which now scales to node bounds): bounding box hit test
    if (isInsideBounds(localPoint, node)) {
      return { hitId: childId, selectableId: childId, immediateChildId: childId };
    }
  }

  return null;
}

// ─── World bounds ─────────────────────────────────────────────────────────

/**
 * Get the world-space bounding box of a node by accumulating parent transforms.
 */
export function getNodeWorldBounds(doc: SceneDocument, nodeId: string): WorldBounds {
  const node = doc.nodes[nodeId];
  if (!node) return { x: 0, y: 0, width: 0, height: 0, rotation: 0 };

  // Build chain from root to node
  const chain: SceneNode[] = [];
  let current: SceneNode | undefined = node;
  while (current) {
    chain.unshift(current);
    current = current.parentId ? doc.nodes[current.parentId] : undefined;
  }

  // Accumulate translation (simplified: only accumulates x/y and rotation of leaf)
  let worldX = 0;
  let worldY = 0;
  let totalRotation = 0;

  for (let i = 0; i < chain.length; i++) {
    const n = chain[i];
    if (i < chain.length - 1) {
      // Parent node: just accumulate position
      // For rotated parents, we need to rotate the child's position
      if (totalRotation !== 0) {
        const rotated = rotatePoint(n.x, n.y, 0, 0, totalRotation);
        worldX += rotated.x;
        worldY += rotated.y;
      } else {
        worldX += n.x;
        worldY += n.y;
      }
      totalRotation += n.rotation ?? 0;
    } else {
      // Leaf node
      if (totalRotation !== 0) {
        const rotated = rotatePoint(n.x, n.y, 0, 0, totalRotation);
        worldX += rotated.x;
        worldY += rotated.y;
      } else {
        worldX += n.x;
        worldY += n.y;
      }
      totalRotation += n.rotation ?? 0;
    }
  }

  const effectiveHeight = node.height > 0 ? node.height
    : (node.type === 'TEXT' ? estimateTextHeight(node) : node.height);

  // PATH nodes now scale to fit node.width x node.height, so use node dims directly

  return {
    x: worldX,
    y: worldY,
    width: node.width,
    height: effectiveHeight,
    rotation: totalRotation,
  };
}

/**
 * Get the accumulated world offset of a node's parent chain.
 * For root-level nodes (no parent), returns (0, 0).
 * Used to convert between world and local coordinates for nested nodes.
 */
export function getParentWorldOffset(doc: SceneDocument, nodeId: string): Point {
  const node = doc.nodes[nodeId];
  if (!node?.parentId) return { x: 0, y: 0 };

  const parentBounds = getNodeWorldBounds(doc, node.parentId);
  // The parent's world position is where its local origin sits in world space
  return { x: parentBounds.x, y: parentBounds.y };
}

/**
 * Get the axis-aligned bounding box of a node in world space.
 * For GROUP/FRAME nodes with zero dimensions, computes bounds from children.
 * Accounts for rotation by computing the rotated corners.
 */
export function getNodeAABB(doc: SceneDocument, nodeId: string): Rect {
  const node = doc.nodes[nodeId];
  const wb = getNodeWorldBounds(doc, nodeId);

  // For GROUP/FRAME nodes with no intrinsic dimensions, derive bounds from children
  if (node && (node.type === 'GROUP' || node.type === 'FRAME') && node.width === 0 && node.height === 0 && node.children?.length) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const childId of node.children) {
      const childAABB = getNodeAABB(doc, childId);
      if (childAABB.width === 0 && childAABB.height === 0) continue;
      minX = Math.min(minX, childAABB.x);
      minY = Math.min(minY, childAABB.y);
      maxX = Math.max(maxX, childAABB.x + childAABB.width);
      maxY = Math.max(maxY, childAABB.y + childAABB.height);
    }
    if (minX !== Infinity) {
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
  }

  if (!wb.rotation) {
    return { x: wb.x, y: wb.y, width: wb.width, height: wb.height };
  }

  // Compute rotated corners
  const cx = wb.x + wb.width / 2;
  const cy = wb.y + wb.height / 2;
  const corners = [
    rotatePoint(wb.x, wb.y, cx, cy, wb.rotation),
    rotatePoint(wb.x + wb.width, wb.y, cx, cy, wb.rotation),
    rotatePoint(wb.x + wb.width, wb.y + wb.height, cx, cy, wb.rotation),
    rotatePoint(wb.x, wb.y + wb.height, cx, cy, wb.rotation),
  ];

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of corners) {
    minX = Math.min(minX, c.x);
    minY = Math.min(minY, c.y);
    maxX = Math.max(maxX, c.x);
    maxY = Math.max(maxY, c.y);
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Union of the world-space AABBs of several nodes — the combined selection box
 * drawn (and resized) when 2+ nodes are selected. `doc` must already be laid
 * out (pass computeLayout(doc)) so auto-layout children report their rendered
 * position, matching every other handle/bounds reader. Returns null when no id
 * resolves to a node.
 */
export function getSelectionAABB(doc: SceneDocument, ids: Iterable<string>): Rect | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of ids) {
    if (!doc.nodes[id]) continue;
    const b = getNodeAABB(doc, id);
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// ─── Marquee selection ────────────────────────────────────────────────────

/**
 * Find all root-level (or entered-group-level) nodes that intersect a marquee rectangle.
 */
export function marqueeSelect(
  doc: SceneDocument,
  marquee: Rect,
  enteredGroupId?: string | null,
  linkZoneMode?: boolean,
): string[] {
  // In link-zone mode, only select zone children
  if (linkZoneMode) {
    const results: string[] = [];
    for (const id of doc.rootChildren) {
      const group = doc.nodes[id];
      if (!group || !group.children?.length) continue;
      if (group.slotRole !== 'link-zone' && group.type !== 'LINK_ZONE_GROUP') continue;
      for (const childId of group.children) {
        const node = doc.nodes[childId];
        if (!node || node.visible === false) continue;
        const aabb = getNodeAABB(doc, childId);
        if (
          aabb.x < marquee.x + marquee.width &&
          aabb.x + aabb.width > marquee.x &&
          aabb.y < marquee.y + marquee.height &&
          aabb.y + aabb.height > marquee.y
        ) {
          results.push(childId);
        }
      }
    }
    return results;
  }

  const results: string[] = [];
  const childIds = enteredGroupId
    ? doc.nodes[enteredGroupId]?.children ?? []
    : doc.rootChildren;

  for (const childId of childIds) {
    const node = doc.nodes[childId];
    if (!node || node.visible === false || node.locked || node.slotRole === 'link-zone') continue;
    if (node.type === 'LINK_ZONE_GROUP' || node.type === 'LINK_ZONE') continue;

    const aabb = getNodeAABB(doc, childId);

    // Check rectangle intersection
    if (
      aabb.x < marquee.x + marquee.width &&
      aabb.x + aabb.width > marquee.x &&
      aabb.y < marquee.y + marquee.height &&
      aabb.y + aabb.height > marquee.y
    ) {
      results.push(childId);
    }
  }

  return results;
}

/**
 * Find the FRAME/GROUP that should receive a dragged node on release.
 * Resolves the way Figma does: take the topmost node under the cursor by
 * render order, then nest into it (if it's a container) or its nearest
 * FRAME/GROUP ancestor. Excludes the dragged node's own subtree
 * (`excludeIds`) so a node can't be nested into itself or its descendants.
 *
 * Render order matters: a sibling subtree that paints later (e.g. an image
 * in the next section overhanging across the boundary) visually covers
 * earlier frames, and the drop must follow what the user sees — picking the
 * smallest containing frame by area can nest the node behind that overhang.
 *
 * Containment uses world AABBs (rotation-agnostic) which is fine for nesting.
 * Returns the container id, or null when the point is over no eligible
 * container. Callers should still skip a no-op (target === current parent).
 */
export function findDropContainer(
  doc: SceneDocument,
  scenePoint: Point,
  excludeIds: Set<string>,
): string | null {
  return resolveDropAtPoint(computeLayout(doc), scenePoint, excludeIds);
}

function resolveDropAtPoint(
  laidOut: SceneDocument,
  scenePoint: Point,
  excludeIds: Set<string>,
): string | null {
  // Reverse paint order: later siblings draw above earlier ones, and a
  // container's children draw above its own fill — so test children before
  // the container body.
  const topmostHit = (childIds: string[]): string | null => {
    for (let i = childIds.length - 1; i >= 0; i--) {
      const id = childIds[i];
      if (excludeIds.has(id)) continue;
      const node = laidOut.nodes[id];
      if (!node || node.visible === false || node.locked || node.slotRole === 'link-zone') continue;
      if (node.type === 'LINK_ZONE_GROUP' || node.type === 'LINK_ZONE') continue;

      const aabb = getNodeAABB(laidOut, id);
      const inside =
        scenePoint.x >= aabb.x &&
        scenePoint.x <= aabb.x + aabb.width &&
        scenePoint.y >= aabb.y &&
        scenePoint.y <= aabb.y + aabb.height;

      if (node.type === 'FRAME' || node.type === 'GROUP') {
        // A clipping container hides children outside its bounds — an
        // invisible overhang must not catch the drop.
        if (node.clipContent && !inside) continue;
        const inner = topmostHit(node.children ?? []);
        if (inner) return inner;
        if (inside) return id;
        continue;
      }

      if (inside) return id;
    }
    return null;
  };

  let hit = topmostHit(laidOut.rootChildren);
  while (hit) {
    const node = laidOut.nodes[hit];
    if (!node) return null;
    if (node.type === 'FRAME' || node.type === 'GROUP') return hit;
    hit = node.parentId ?? null;
  }
  return null;
}

/**
 * Drop-container resolution for a dragged node, probing its whole footprint
 * instead of a single cursor point.
 *
 * A single-point answer breaks when the node straddles two stacked sections:
 * the cursor picks one, but the node's bounds overlap a sibling that paints
 * later, and the overlapped part vanishes behind it. Probing the center and
 * corners surfaces every container the node visually touches; the one that
 * paints LAST is the only choice nothing else can occlude — nesting there
 * keeps the node looking exactly as it did during the drag.
 */
/**
 * The top-level band an id belongs to on a single-artboard email/social page:
 * walk up to the node that is a direct child of the page root — that node IS
 * the band (a node that is itself a top-level child returns itself). Null on
 * the multi-root workspace. Used to keep a free-drag from silently re-homing
 * nested content into a sibling band.
 */
function bandOf(doc: SceneDocument, id: string): string | null {
  if (doc.rootChildren.length !== 1) return null;
  const rootId = doc.rootChildren[0];
  let cur = doc.nodes[id];
  while (cur) {
    if (cur.parentId === rootId) return cur.id;
    if (!cur.parentId) return null;
    cur = doc.nodes[cur.parentId];
  }
  return null;
}

export function findDropContainerForNode(
  doc: SceneDocument,
  nodeId: string,
  excludeIds: Set<string>,
): string | null {
  const laidOut = computeLayout(doc);
  const aabb = getNodeAABB(laidOut, nodeId);

  // Inset the corner probes so a neighbor merely kissing an edge doesn't
  // catch the drop.
  const ix = Math.min(aabb.width / 4, 8);
  const iy = Math.min(aabb.height / 4, 8);
  const probes: Point[] = [
    { x: aabb.x + aabb.width / 2, y: aabb.y + aabb.height / 2 },
    { x: aabb.x + ix, y: aabb.y + iy },
    { x: aabb.x + aabb.width - ix, y: aabb.y + iy },
    { x: aabb.x + ix, y: aabb.y + aabb.height - iy },
    { x: aabb.x + aabb.width - ix, y: aabb.y + aabb.height - iy },
  ];

  const candidates = new Set<string>();
  for (const p of probes) {
    const c = resolveDropAtPoint(laidOut, p, excludeIds);
    if (c) candidates.add(c);
  }
  if (candidates.size === 0) return null;

  // Band containment: a node NESTED inside a band must not be re-homed into a
  // DIFFERENT top-level band by a free-drag (the "text jumped into the next
  // section" bug); it may still nest into sub-frames of its own band. A
  // top-level node (its own band) stays unrestricted — it can be dropped into
  // any band.
  const nb = bandOf(doc, nodeId);
  const ownBand = nb && nb !== nodeId ? nb : null;
  if (ownBand) {
    for (const c of [...candidates]) {
      if (bandOf(doc, c) !== ownBand) candidates.delete(c);
    }
    if (candidates.size === 0) return null;
  }

  if (candidates.size === 1) return [...candidates][0];

  // Document paint order: pre-order DFS index. Higher = paints later.
  const paintIndex = new Map<string, number>();
  let i = 0;
  const index = (ids: string[]): void => {
    for (const id of ids) {
      const node = laidOut.nodes[id];
      if (!node) continue;
      paintIndex.set(id, i++);
      if (node.children?.length) index(node.children);
    }
  };
  index(laidOut.rootChildren);

  let best: string | null = null;
  let bestIdx = -1;
  for (const c of candidates) {
    const idx = paintIndex.get(c) ?? -1;
    if (idx > bestIdx) {
      bestIdx = idx;
      best = c;
    }
  }
  return best;
}

/**
 * Resolve which frame a newly-inserted node should be parented into, matching
 * Figma's "position decides structure" rule: a selected frame the insertion
 * point falls within receives the new child, otherwise the deepest Section/frame
 * under the insertion point. Returns null when nothing more specific than the
 * page itself applies (callers then fall back to their default root placement) —
 * including for the multi-root workspace, where adds stay root-level siblings.
 *
 * `point` is the new node's intended center in world/doc space.
 */
export function resolveInsertParent(
  doc: SceneDocument,
  selectedIds: ReadonlySet<string>,
  point: Point,
): string | null {
  // Single-artboard docs only (the email/social page). The infinite-canvas
  // workspace has many root frames and keeps inserts at the root.
  if (doc.rootChildren.length !== 1) return null;
  const rootId = doc.rootChildren[0];
  const root = doc.nodes[rootId];
  if (
    !root ||
    root.type !== 'FRAME' ||
    root.frameCanvasId ||
    root.pluginData?.['isArtboard'] !== true
  ) {
    return null;
  }

  const laidOut = computeLayout(doc);
  const contains = (id: string): boolean => {
    const b = getNodeAABB(laidOut, id);
    return point.x >= b.x && point.x <= b.x + b.width && point.y >= b.y && point.y <= b.y + b.height;
  };

  // 1. A selected container the insertion point falls within wins, so
  //    "select a section, add a heading" drops the heading into that section.
  if (selectedIds.size === 1) {
    const sel = doc.nodes[[...selectedIds][0]];
    if (
      sel &&
      (sel.type === 'FRAME' || sel.type === 'GROUP') &&
      sel.id !== rootId &&
      contains(sel.id)
    ) {
      return sel.id;
    }
  }

  // 2. Deepest real frame under the insertion point. The page artboard itself
  //    is not "more specific" than the default, so fall through to null there.
  const contained = findDropContainer(doc, point, new Set());
  if (contained && contained !== rootId) return contained;

  return null;
}
